/**
 * ZAdminGmail - Zod Validation Schemas for Admin Gmail API
 *
 * Single source of truth for Admin Gmail domain API validation.
 * These schemas generate both runtime validation (Zod) and compile-time types (TApi).
 *
 * Naming: Z prefix = Zod schemas (vs TApi = TypeScript types, DbEntity = Prisma)
 *
 * NOT admin-guarded, despite the path: the route is `@PublicAPI` (org-scoped
 * API-key auth) and the use case filters on the caller's own organizationId.
 */

import { z } from "zod";

import { ApiSuccessSchema } from "../../common/response.schema";
import { WatchSetupDetailSchema } from "./zadmin-email-watch";

// ═══════════════════════════════════════════════════════════════
// RESPONSE SCHEMAS - Output validation
// ═══════════════════════════════════════════════════════════════

/**
 * POST /setup-watches - Setup Gmail watches response
 */
export const SetupWatchesResponseSchema = z.object({
  /** Total Gmail deployments found */
  total: z.number().int().min(0),
  /** Number of successful setups */
  setupSuccessful: z.number().int().min(0),
  /** Number of skipped (already have watch) */
  skipped: z.number().int().min(0),
  /** Number of failed setups */
  failed: z.number().int().min(0),
  /** Detailed results per deployment */
  details: z.array(WatchSetupDetailSchema)
});

// ═══════════════════════════════════════════════════════════════
// ZADMINGMAIL MANIFEST - Export all schemas for runtime use
// ═══════════════════════════════════════════════════════════════

/**
 * ZAdminGmail - Zod API validation schemas
 *
 * Single source of truth for Admin Gmail API:
 * - Runtime validation (backend + frontend)
 * - Type generation (TApi auto-inferred via InferSchemas)
 *
 * Z prefix = Zod schemas (vs DbEntity = Prisma, TApi = TypeScript)
 */
export const ZAdminGmail = {
  /**
   * Setup Gmail Pub/Sub watches for all deployments missing them
   * POST /api/admin/gmail/setup-watches
   */
  SetupWatches: {
    method: "POST" as const,
    path: "/api/admin/gmail/setup-watches",
    Response: ApiSuccessSchema(SetupWatchesResponseSchema)
  }
} as const;

// ═══════════════════════════════════════════════════════════════
// TYPE EXPORTS - For convenience
// ═══════════════════════════════════════════════════════════════

/** Setup watches response */
export type SetupWatchesResponse = z.infer<typeof SetupWatchesResponseSchema>;
